import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Loader, Sparkles, ExternalLink, Trash2,
  HelpCircle, CheckCircle2, AlertCircle, User as UserIcon,
  Building2, ArrowLeft,
} from "lucide-react";
import Navbar from "../components/Navbar";
import ProgressMeter from "../components/ProgressMeter";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Interviewer Research — Perplexity-powered pre-interview briefing.
 *
 * Flow:
 *   1. User submits either a LinkedIn URL OR a name + company pair.
 *   2. Backend calls Perplexity Sonar Pro (web-grounded) with structured JSON.
 *   3. We render the briefing + talking points + likely questions + dos/don'ts
 *      with citations (clickable source links).
 *   4. Previous briefings show up in a side list for quick re-open.
 */
export default function InterviewerResearch() {
  const navigate = useNavigate();
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [roleContext, setRoleContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/interviewer/briefings");
      setHistory(data.briefings || []);
    } catch (_e) {
      // Silent — fresh accounts have no history yet.
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!linkedinUrl && !(name && company)) {
      setError("Provide either a LinkedIn URL or both name + company.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/interviewer/research", {
        linkedin_url: linkedinUrl || null,
        name: name || null,
        company: company || null,
        role_context: roleContext || null,
      });
      setResult(data);
      await loadHistory();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't generate briefing.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this briefing?")) return;
    try {
      await api.delete(`/interviewer/briefings/${id}`);
      if (result?.id === id) setResult(null);
      await loadHistory();
    } catch (_e) {
      // ignore
    }
  };

  const openHistoryBriefing = async (id) => {
    setError("");
    try {
      const { data } = await api.get(`/interviewer/briefings/${id}`);
      setResult({ ...data, cached: true });
      setLinkedinUrl(data.linkedin_url || "");
      setName(data.name || "");
      setCompany(data.company || "");
      setRoleContext(data.role_context || "");
    } catch (_e) {
      setError("Couldn't load that briefing.");
    }
  };

  return (
    <div className="min-h-screen rm-paper" data-testid="interviewer-research-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="font-mono-rm uppercase text-[10px] tracking-widest mb-3 inline-flex items-center gap-1"
          style={{ color: "#475569", fontWeight: 700 }}
          data-testid="interviewer-back-btn"
        >
          <ArrowLeft size={11} /> Back
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest rm-text-orange mb-1"
               style={{ fontWeight: 700 }}>
            · NEW · Powered by Perplexity Sonar Pro
          </div>
          <h1 className="font-display uppercase leading-none mb-2"
              style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.5rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}
              data-testid="interviewer-title">
            Interviewer <span className="rm-text-orange">Research</span>
          </h1>
          <p className="text-sm md:text-base max-w-3xl" style={{ color: "#334155" }}>
            Paste a LinkedIn URL (or just their name + company) and Resmo will
            web-search public sources to produce a briefing, talking points,
            likely questions, and dos &amp; don'ts — so you walk into the
            conversation already two steps ahead.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 md:gap-8">
          {/* LEFT — form + result */}
          <div className="lg:col-span-8 space-y-5">
            {/* Form */}
            <form onSubmit={handleSubmit}
                  className="rm-card-static p-5 md:p-6"
                  style={{ background: "white", borderColor: "#0F1B2D", boxShadow: "4px 4px 0 0 #FF6A00" }}
                  data-testid="interviewer-form">
              <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-3"
                   style={{ color: "#0F1B2D", fontWeight: 700 }}>
                · Who are you meeting?
              </div>
              <div className="space-y-3">
                <div>
                  <label className="rm-label text-[11px]">LinkedIn URL (recommended)</label>
                  <input
                    type="url"
                    placeholder="https://www.linkedin.com/in/jane-doe"
                    className="rm-input w-full"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    data-testid="interviewer-linkedin-input"
                  />
                </div>
                <div className="text-center font-mono-rm text-[10px] uppercase tracking-widest"
                     style={{ color: "#94A3B8" }}>
                  · or ·
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="rm-label text-[11px]">Name</label>
                    <input
                      placeholder="Jane Doe"
                      className="rm-input w-full"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="interviewer-name-input"
                    />
                  </div>
                  <div>
                    <label className="rm-label text-[11px]">Company</label>
                    <input
                      placeholder="Acme Corp"
                      className="rm-input w-full"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      data-testid="interviewer-company-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="rm-label text-[11px]">Role context (optional)</label>
                  <input
                    placeholder="e.g. interviewing for Senior PM, technical round"
                    className="rm-input w-full"
                    value={roleContext}
                    onChange={(e) => setRoleContext(e.target.value)}
                    data-testid="interviewer-role-context-input"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button
                  type="submit"
                  disabled={loading || (!linkedinUrl && !(name && company))}
                  className="rm-btn rm-btn-primary text-sm px-4 py-2.5"
                  data-testid="interviewer-submit-btn"
                >
                  {loading ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
                  {loading ? "Researching…" : "Generate briefing"}
                </button>
                <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                      style={{ color: "#475569" }}>
                  · 10 credits · Pro free · 24h cache
                </span>
              </div>
              {loading && (
                <div className="mt-3 max-w-sm">
                  <ProgressMeter expectedSeconds={45} label="Researching your interviewer" testid="interviewer-progress-meter" />
                </div>
              )}

              {error && (
                <div className="mt-3 rm-card-static p-2 text-xs font-mono-rm"
                     style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                     data-testid="interviewer-error">
                  · {error}
                </div>
              )}
            </form>

            {/* Result */}
            {result && <BriefingCard result={result} />}

            {!result && !loading && history.length === 0 && (
              <div className="rm-card-static p-5"
                   style={{ background: "#FFFBEB", border: "2px dashed #FFC145" }}
                   data-testid="interviewer-empty-hint">
                <div className="flex items-start gap-3">
                  <Sparkles size={18} style={{ color: "#FF6A00", flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div className="font-display uppercase text-base leading-tight"
                         style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
                      First time? Try researching a known leader.
                    </div>
                    <p className="text-[13px] mt-1" style={{ color: "#475569" }}>
                      Paste the LinkedIn URL of your actual interviewer, or
                      try a public figure to see what Resmo produces — e.g.
                      "Satya Nadella" at "Microsoft" with role context
                      "Senior PM".
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — history */}
          <div className="lg:col-span-4">
            <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
                 style={{ color: "#0F1B2D", fontWeight: 700 }}>
              · Previous briefings
            </div>
            {history.length === 0 ? (
              <div className="rm-card-static p-3 text-xs font-mono-rm uppercase tracking-widest"
                   style={{ background: "#F8FAFC", color: "#94A3B8", borderColor: "#CBD5E1" }}
                   data-testid="interviewer-history-empty">
                · No briefings yet
              </div>
            ) : (
              <div className="space-y-2" data-testid="interviewer-history-list">
                {history.map((b) => (
                  <div key={b.id}
                       className="rm-card-static p-3"
                       style={{ background: "white", borderColor: "#0F1B2D", boxShadow: "2px 2px 0 0 #FFC145" }}
                       data-testid={`interviewer-history-${b.id}`}>
                    <button
                      type="button"
                      onClick={() => openHistoryBriefing(b.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <UserIcon size={12} style={{ color: "#FF6A00" }} />
                        <div className="font-display uppercase text-sm leading-tight"
                             style={{ color: "#0F1B2D" }}>
                          {b.name || "Interviewer"}
                        </div>
                      </div>
                      {b.company && (
                        <div className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                             style={{ color: "#475569" }}>
                          <Building2 size={10} /> {b.company}
                        </div>
                      )}
                      <p className="text-[11.5px] mt-1 leading-snug line-clamp-2"
                         style={{ color: "#475569" }}>
                        {(b.briefing || "").slice(0, 110)}…
                      </p>
                    </button>
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleDelete(b.id)}
                        className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                        data-testid={`interviewer-delete-${b.id}`}
                      >
                        <Trash2 size={10} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  BriefingCard — renders the structured Perplexity output            */
/* ────────────────────────────────────────────────────────────────── */
function BriefingCard({ result }) {
  // Citation [N] tokens in copy can link out to the matching citations array.
  const renderWithCitations = (text) => {
    if (!text) return null;
    const parts = String(text).split(/(\[\d+\])/g);
    return parts.map((p, i) => {
      const m = p.match(/^\[(\d+)\]$/);
      if (m) {
        const idx = parseInt(m[1], 10) - 1;
        const c = result.citations?.[idx];
        if (c?.url) {
          return (
            <a key={i} href={c.url} target="_blank" rel="noreferrer"
               className="font-mono-rm text-[10px] uppercase tracking-widest mx-0.5"
               style={{ color: "#0E5A4A", verticalAlign: "super" }}
               title={c.title}>
              [{m[1]}]
            </a>
          );
        }
      }
      return <span key={i}>{p}</span>;
    });
  };

  const Section = ({ icon, title, items, accent, testid }) => (
    <div
      className="rm-card-static p-4"
      style={{ background: "white", borderColor: "#0F1B2D", boxShadow: `3px 3px 0 0 ${accent}` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
          style={{ color: accent, fontWeight: 700 }}
        >
          {icon} · {title}
        </div>
        <span className="font-mono-rm text-[10px] uppercase tracking-widest ml-auto"
              style={{ color: "#94A3B8" }}>
          {items.length}
        </span>
      </div>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="text-[13px] leading-snug pl-3 relative" style={{ color: "#334155" }}>
            <span style={{ position: "absolute", left: 0, top: 6, width: 4, height: 4, borderRadius: "50%", background: accent }} />
            {renderWithCitations(item)}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="interviewer-briefing-result">
      {/* Header card */}
      <div
        className="rm-card-static p-5"
        style={{
          background: "linear-gradient(135deg, #0F1B2D 0%, #1E3A5F 100%)",
          borderColor: "#0F1B2D",
          boxShadow: "5px 5px 0 0 #FF6A00",
        }}
      >
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest"
               style={{ color: "#FFC145", fontWeight: 700 }}>
            · The briefing
          </div>
          {result.cached && (
            <span className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                  style={{ background: "#FFC145", color: "#0F1B2D", fontWeight: 700 }}>
              · Cached
            </span>
          )}
          <span className="font-mono-rm text-[9px] uppercase tracking-widest ml-auto"
                style={{ color: "#CBD5E1" }}>
            {result.name ? `${result.name}${result.company ? " · " + result.company : ""}`
                         : (result.linkedin_url || "")}
          </span>
        </div>
        <p className="text-sm md:text-base leading-relaxed" style={{ color: "white" }}>
          {renderWithCitations(result.briefing)}
        </p>
      </div>

      {/* 2x2 grid of sections */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section
          icon={<Sparkles size={11} />}
          title="Talking points"
          items={result.talking_points || []}
          accent="#FF6A00"
          testid="interviewer-talking-points"
        />
        <Section
          icon={<HelpCircle size={11} />}
          title="Likely questions"
          items={result.questions_they_ask || []}
          accent="#1E40AF"
          testid="interviewer-questions"
        />
        <Section
          icon={<CheckCircle2 size={11} />}
          title="Dos & Don'ts"
          items={result.dos_and_donts || []}
          accent="#047857"
          testid="interviewer-dos-donts"
        />
        {result.citations && result.citations.length > 0 && (
          <div
            className="rm-card-static p-4"
            style={{ background: "white", borderColor: "#0F1B2D", boxShadow: "3px 3px 0 0 #94A3B8" }}
            data-testid="interviewer-citations"
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                style={{ color: "#0E5A4A", fontWeight: 700 }}
              >
                <ExternalLink size={11} /> · Sources
              </div>
              <span className="font-mono-rm text-[10px] uppercase tracking-widest ml-auto"
                    style={{ color: "#94A3B8" }}>
                {result.citations.length}
              </span>
            </div>
            <ol className="space-y-1 text-[12px]" style={{ color: "#334155" }}>
              {result.citations.map((c, i) => (
                <li
                  key={i}
                  className="flex items-start gap-1.5"
                  style={{ minWidth: 0, wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  <span className="font-mono-rm text-[10px]" style={{ color: "#0E5A4A", flexShrink: 0, paddingTop: 2 }}>
                    [{i + 1}]
                  </span>
                  {c.url ? (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline decoration-dotted underline-offset-2 leading-snug"
                      style={{
                        color: "#0F1B2D",
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        maxWidth: "100%",
                      }}
                    >
                      {c.title || c.url}
                    </a>
                  ) : (
                    <span
                      style={{
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {c.title}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Honesty disclaimer */}
      <div
        className="rm-card-static p-3"
        style={{ background: "#FEF3C7", border: "2px dashed #CA8A04", color: "#78350F" }}
      >
        <div className="flex items-start gap-2">
          <AlertCircle size={14} style={{ color: "#CA8A04", flexShrink: 0, marginTop: 2 }} />
          <p className="text-[12px] leading-snug">
            <strong>Use as a starting point.</strong> Resmo's interviewer briefings
            are based on public web information and inferred patterns — they may
            be incomplete or out of date. Treat "likely questions" as educated
            guesses, not guarantees. Always blend with your own research before
            the interview.
          </p>
        </div>
      </div>
    </div>
  );
}
